// Type imports
import type { ColumnDef } from "@tanstack/react-table";

// Value imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconBin, IconCross, IconTick } from "@/newComponents/icon/Icon";
import ExpandableTable from "@/newComponents/table/ExpandableTable";
import useWindowDimensions from "@/newComponents/utils/useWindowDimensions";
import { useUIStateStore } from "@/store/uiStateStore";

import { Button } from "../../../../../../newComponents/button/Button";
import { FormCheckbox } from "../../../../../../newComponents/input/FormCheckbox";
import { ScrollArea } from "../../../../../../newComponents/scrollArea/ScrollArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../../newComponents/select/Select";
// import Table from "../../../../../../newComponents/table/Table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../../../newComponents/tabs/Tabs";
import Text from "../../../../../../newComponents/typography/Text";
import useAlbumStore from "../store/albumStore";
import useSongWriterAssignmentStore from "../store/songWriterAssignmentStore";
import useWriterStore from "../store/writerStore";

// Type for table row data
type SongWriterRow = {
  songId: string;
  songTitle: string;
  artistName: string;
  duration: string;
  [key: string]: unknown; // For dynamic writer columns
};

export default function AssignWritersStep() {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { t } = useTranslation("album");
  const { isBelowMd } = useWindowDimensions();

  const { tracks } = useAlbumStore();
  const { selectedWriters } = useWriterStore();
  const assignments = useSongWriterAssignmentStore(state => state.assignments);
  const initializeMatrix = useSongWriterAssignmentStore(state => state.initializeMatrix);
  const toggleWriterForSong = useSongWriterAssignmentStore(state => state.toggleWriterForSong);
  const assignWriterToAllSongs = useSongWriterAssignmentStore(state => state.assignWriterToAllSongs);
  const assignAllWritersToSong = useSongWriterAssignmentStore(state => state.assignAllWritersToSong);

  // Add state to force re-renders when checkboxes change
  const [_forceRender, setForceRender] = useState(false);

  // Get selected tracks only
  const selectedTracks = tracks.filter(track => track.isSelected);

  // Use a ref to track initialization to prevent infinite loops
  const initializationKey = useRef<string>("");

  // State for active tab
  const [activeTab, setActiveTab] = useState<string>("song-by-song");

  // Initialize the assignment matrix when component mounts or data changes
  useEffect(() => {
    if (selectedTracks.length > 0 && selectedWriters.length > 0) {
      // Create a key based on the current selection to avoid unnecessary re-initialization
      const currentKey = `${selectedTracks.map(t => t.id).join(",")}-${selectedWriters.map(w => w.writercode).join(",")}`;

      if (initializationKey.current !== currentKey) {
        initializeMatrix(selectedTracks, selectedWriters);
        initializationKey.current = currentKey;
      }
    }
  }, [selectedTracks, selectedWriters, initializeMatrix]);

  // Format track duration from milliseconds to MM:SS
  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Prepare data for the table
  const tableData = useMemo<SongWriterRow[]>(() => {
    return selectedTracks.map(track => ({
      songId: track.id,
      songTitle: track.name,
      artistName: track.artists.map(artist => artist.name).join(", "),
      duration: formatDuration(track.duration_ms),
    }));
  }, [selectedTracks]);

  // Create stable callback functions to prevent re-renders
  const handleToggleWriterForSong = useCallback((songId: string, writerCode: string) => {
    console.log(`handleToggleWriterForSong called for song ${songId} and writer ${writerCode}`);
    toggleWriterForSong(songId, writerCode);
    // Force a re-render to ensure the checkboxes update
    setForceRender(prev => !prev);
  }, [toggleWriterForSong]);

  const handleAssignWriterToAllSongs = useCallback((writerCode: string, checked: boolean) => {
    assignWriterToAllSongs(writerCode, checked);
  }, [assignWriterToAllSongs]);

  const handleAssignAllWritersToSong = useCallback((songId: string, checked: boolean) => {
    assignAllWritersToSong(songId, checked);
  }, [assignAllWritersToSong]);

  // Define table columns dynamically based on selected writers
  const columns = useMemo<ColumnDef<SongWriterRow>[]>(() => {
    const baseColumns: ColumnDef<SongWriterRow>[] = [
      {
        id: "song_title",
        enableSorting: false,
        // header: "Song Title",
        header: t("song-title") ?? "Song Title",
        accessorKey: "songTitle",
        // Pin the song title column to the left
        enablePinning: true,
        cell: ({ row }) => {
          const songAssignments = assignments[row.original.songId];
          const allWritersAssigned = selectedWriters.length > 0
            && typeof songAssignments !== "undefined"
            && selectedWriters.every(writer => songAssignments[writer.writercode]);

          return (
            <div className="min-w-[100px] lg:min-w-[170px] flex-1 flex items-center gap-3">
              <Text as="p" className="flex-1 text-sm font-semibold">
                {row.original.songTitle}
              </Text>
              <div
                className="cursor-pointer"
                onClick={() => handleAssignAllWritersToSong(row.original.songId, !allWritersAssigned)}
              >
                <FormCheckbox
                  id={`checkbox-all-writers-${row.original.songId}`}
                  checked={Boolean(allWritersAssigned)}
                  onChange={e => handleAssignAllWritersToSong(row.original.songId, e.target.checked)}
                  className="w-5 h-5 cursor-pointer "
                />
              </div>
            </div>
          );
        },
      },
    ];

    // Add columns for each selected writer
    const writerColumns: ColumnDef<SongWriterRow>[] = selectedWriters.map(writer => ({
      id: `writer_${writer.writercode}`,
      header: ({ table }) => {
        const isAllChecked = table.getRowModel().rows.length > 0
          && table.getRowModel().rows.every(row =>
            assignments[row.original.songId]?.[writer.writercode] || false,
          );

        // Create display name from writername (fallback since SelectedWriter type doesn't have firstname/lastname)
        const displayName = writer.writername;
        const ipiNumber = writer.caeipinumber ?? "No IPI";

        return (
          <div className="flex items-center justify-center w-full h-full p-1 sm:p-2 min-w-32 sm:min-w-64">
            <button
              type="button"
              className="flex items-center justify-center w-full h-full transition-colors rounded hover:bg-white/10"
              onClick={() => handleAssignWriterToAllSongs(writer.writercode, !isAllChecked)}
              // I know how much the below code sucks to look at, please dont @ me - Matt
              title={`${isAllChecked ? (t("remove") ?? "Remove") : (t("assign") ?? "Assign")} ${displayName} ${isAllChecked ? (t("from") ?? "from") : (t("to") ?? "to")} ${t("all-songs") ?? "all songs"}`}
              aria-label={`${isAllChecked ? (t("remove") ?? "Remove") : (t("assign") ?? "Assign")} ${displayName} ${isAllChecked ? (t("from") ?? "from") : (t("to") ?? "to")} ${t("all-songs") ?? "all songs"}`}
            >
              <FormCheckbox
                tableHeader={true}
                checked={isAllChecked}
                onChange={() => {}} // Read-only, controlled by button
                className="flex-shrink-0 w-3 h-3 mr-1 pointer-events-none sm:w-4 sm:h-4 sm:mr-2"
              />
              <div className="flex-1 min-w-0 text-left">
                <Text as="small" className="block text-xs font-medium leading-tight text-white uppercase sm:text-sm">
                  {displayName}
                </Text>
                <Text as="small" className="hidden text-xs leading-tight sm:block text-white/70">
                  {ipiNumber}
                </Text>
              </div>
            </button>
          </div>
        );
      },
      cell: ({ row }) => {
        const isAssigned = assignments[row.original.songId]?.[writer.writercode] || false;

        return (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // Stop event propagation
                handleToggleWriterForSong(row.original.songId, writer.writercode);
              }}
              className={`w-full h-6 sm:h-8 rounded-md transition-colors duration-200 font-medium text-lg sm:text-2xl flex items-center justify-center ${
                isAssigned
                  ? "bg-sentric-teal text-white hover:bg-sentric-teal/50"
                  : "border border-sentric-dark-background/25  hover:bg-sentric-mid-grey/50 text-black"
              }`}
            >
              {isAssigned ? <IconTick className="w-4 h-4 sm:w-6 sm:h-6 stroke-white" /> : <IconCross className="w-4 h-4 sm:w-6 sm:h-6 stroke-black" />}
            </button>
          </div>
        );
      },
    }));

    // Return the columns in the correct order: song_title, then all writer columns
    return [...baseColumns, ...writerColumns];
  }, [
    selectedWriters,
    assignments,
    handleAssignWriterToAllSongs,
    handleToggleWriterForSong,
    handleAssignAllWritersToSong,
    t,
  ]);

  // Calculate stats for the table header
  const statsBlock = useMemo(() => {
    const songsWithNoWriters = selectedTracks.filter((track) => {
      const songAssignments = assignments[track.id];
      // Check if song has no writers assigned
      return songAssignments == null || selectedWriters.every(writer => !songAssignments[writer.writercode]);
    }).length;

    return [
      // { title: "Total Songs", value: selectedTracks.length || 0, highlight: false },
      { title: t("total-songs") ?? "Total Songs", value: selectedTracks.length || 0, highlight: false },
      // { title: "Songs with no writers assigned", value: songsWithNoWriters, highlight: true },
      { title: t("songs-with-no-writers-assigned") ?? "Songs with no writers assigned", value: songsWithNoWriters, highlight: true },
    ];
  }, [selectedTracks, assignments, selectedWriters, t]);

  if (selectedTracks.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className="text-gray-500">
          {/* No songs selected. Please go back to step 2 and select some songs. */}
          {t("no-songs-selected")}
        </Text>
      </div>
    );
  }

  if (selectedWriters.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className="text-gray-500">
          {/* No writers selected. Please go back to step 3 and select some writers. */}
          {t("no-writers-selected")}
        </Text>
      </div>
    );
  }

  return (

    <div className="w-full min-w-0">
      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="song-by-song">
            {isBelowMd === true ? (t("individual") ?? "Individual") : (t("individual-song-assignment") ?? "Individual Song Assignment")}
          </TabsTrigger>

          <TabsTrigger value="table">
            {isBelowMd === true ? (t("bulk") ?? "Bulk") : (t("bulk-assignment-table") ?? "Bulk Assignment Table")}
          </TabsTrigger>
        </TabsList>

        {/* Table View Tab */}
        <TabsContent value="table" className="w-full mt-6 space-y-6">
          {/* Assignment table wrapper with overflow handling */}
          <div className="w-full overflow-x-auto shadow-md">
            <ExpandableTable
              canExpandToDialog={true}
              hasFilters={false}
              data={tableData}
              columns={columns}
              enableColumnPinning={true}
              paginationComponent={false}
              pagination={{ pageIndex: 0, pageSize: tracks.length || 1000 }}
              statsBlock={statsBlock}
            />
          </div>
        </TabsContent>

        {/* Individual Song Assignment 2.0 Tab */}
        <TabsContent value="song-by-song" className="mt-6 space-y-6">
          <ScrollArea className={`h-[800px] w-full rounded-lg shadow-md ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-sentric-light-grey p-4 rounded-lg text-old-sentric-dark-gray"}`}>
            <div className="space-y-4">
              {selectedTracks.map((track) => {
                const songAssignments = assignments[track.id];
                // Always read directly from assignments matrix, not from songBysongSelections
                const assignedWriters = selectedWriters.filter(writer =>
                  songAssignments?.[writer.writercode],
                );
                const unassignedWriters = selectedWriters.filter(writer =>
                  !songAssignments?.[writer.writercode],
                );
                const allWritersAssigned = selectedWriters.length > 0
                  && selectedWriters.every(writer => songAssignments?.[writer.writercode]);

                return (
                  <div key={track.id} className={`p-4 rounded-lg shadow-sm transition-colors ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}>
                    <div className="space-y-4">
                      {/* Song info */}
                      <div>
                        <Text as="subtitle">{track.name}</Text>
                        <div className="flex items-center gap-4">
                          <Text as="p">
                            {/* Artist: */}
                            {t("artist") ?? "Artist"}
                            :
                            {" "}
                            {track.artists.map(artist => artist.name).join(", ")}
                          </Text>
                          <Text as="p">
                            {/* Duration: */}
                            {t("duration") ?? "Duration"}
                            :
                            {" "}
                            {formatDuration(track.duration_ms)}
                          </Text>
                        </div>
                      </div>

                      {/* Select All Checkbox */}
                      <div className="flex items-center gap-2">
                        <FormCheckbox
                          id={`select-all-${track.id}`}
                          checked={allWritersAssigned}
                          onChange={e => handleAssignAllWritersToSong(track.id, e.target.checked)}
                          // label="Select all writers"
                          label={t("select-all-writers") ?? "Select all writers"}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* Writer Dropdown */}
                      <div className="space-y-2">
                        <Text as="p" className="text-sm font-medium">
                          {t("add-writer") ?? "Add writer"}
                          :
                        </Text>
                        <Select
                          disabled={allWritersAssigned || unassignedWriters.length === 0}
                          value=""
                          onValueChange={(writerCode) => {
                            if (writerCode) {
                              handleToggleWriterForSong(track.id, writerCode);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={
                              allWritersAssigned
                                ? (t("all-writers-selected") ?? "All writers selected")
                                : unassignedWriters.length === 0
                                  ? (t("no-writers-available") ?? "No writers available")
                                  : (t("choose-writer-to-add") ?? "Choose a writer to add...")
                            }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="-1" disabled>
                              {t("choose-writer-to-add") ?? "Choose a writer to add..."}
                            </SelectItem>
                            {unassignedWriters.map(writer => (
                              <SelectItem key={writer.writercode} value={writer.writercode}>
                                {writer.writername}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Assigned writers list */}
                      <div className="space-y-2">
                        <Text as="p" className="text-sm font-medium">
                          {t("assigned-writers")}
                          {" "}
                          (
                          {assignedWriters.length}
                          )
                        </Text>
                        <div className="space-y-2 ">
                          {assignedWriters.length === 0 ? (
                            <Text as="p" className="text-sm italic text-gray-500">
                              {/* No writers assigned */}
                              {t("no-writers-assigned")}
                            </Text>
                          ) : (
                            assignedWriters.map(writer => (
                              <div
                                key={writer.writercode}
                                className={`flex items-center justify-between p-2 rounded-md ${isTunecore ? "bg-[#282828]" : "bg-sentric-light-grey"}`}
                              >
                                <Text as="p" className="text-sm font-medium">
                                  {writer.writername}
                                </Text>
                                <Button
                                  icon={<IconBin className={`w-5 h-5 ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                                  variant="link"
                                  onClick={() => handleToggleWriterForSong(track.id, writer.writercode)}
                                  className="w-6 h-6 p-0 group hover:scale-110"
                                />
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
